import { AxiosInstance } from 'axios';
import { ListMarketsParams, Market } from '../types/markets';
import { Tag } from '../types/tags';
import { parseJsonArray, toNumberArray } from '../util/normalize';
import { buildOffsetParams } from '../util/pagination';
import { ListMarketsParamsSchema, MarketSchema } from '../z/markets';
import { TagSchema } from '../z/tags';

export class MarketsClient {
  constructor(private readonly http: AxiosInstance) {}

  /**
   * List all markets with optional filtering and pagination.
   * 
   * This endpoint returns a paginated list of markets with optional filtering
   * by active status, event ID, category, and tags. The response includes
   * normalized data with JSON-encoded arrays automatically parsed.
   * 
   * @example
   * ```typescript
   * // Get active markets
   * const markets = await client.markets.list({ active: true, limit: 10 });
   * 
   * // Get markets for a specific event
   * const eventMarkets = await client.markets.list({ eventId: '123' });
   * 
   * // Get markets with specific tags
   * const taggedMarkets = await client.markets.list({ tags: ['politics', 'elections'] });
   * ```
   * 
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of markets to return (default: 10)
   * @param params.offset - Number of markets to skip (default: 0)
   * @param params.active - Filter by active status (true/false)
   * @param params.eventId - Filter by event ID
   * @param params.category - Filter by category
   * @param params.tags - Filter by tags (array of tag IDs or slugs)
   * 
   * @returns Promise<Market[]> - Array of markets with normalized data
   * @throws {Error} When the API request fails or returns invalid data
   * 
   * @see {@link https://docs.polymarket.com/api-reference/markets/list-markets} - API Documentation
   */
  async list(params?: ListMarketsParams): Promise<Market[]> {
    const validatedParams = params ? ListMarketsParamsSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
    try {
      const response = await this.http.get('/markets', { params: queryParams });
      const validatedData = MarketSchema.array().parse(response.data);

      // Normalize JSON-encoded arrays in market data
      return validatedData.map((market) => this.normalizeMarket(market));
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list markets: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a market by its unique identifier.
   * 
   * This endpoint returns detailed information about a specific market,
   * including normalized outcomes, prices, and token IDs. JSON-encoded
   * arrays are automatically parsed for easier consumption.
   * 
   * @example
   * ```typescript
   * const market = await client.markets.getById('123');
   * console.log('Market Title:', market.title);
   * console.log('Outcomes:', market.outcomes); // ['Yes', 'No'] - automatically parsed
   * console.log('Prices:', market.outcomePrices); // [0.6, 0.4] - automatically parsed
   * console.log('Volume:', market.volume);
   * ```
   * 
   * @param id - Market ID
   * @returns Promise<Market> - Market data with normalized arrays
   * @throws {Error} When the market is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/markets/get-market-by-id} - API Documentation
   */
  async getById(id: string): Promise<Market> {
    try {
      const response = await this.http.get(`/markets/${id}`);
      const validatedData = MarketSchema.parse(response.data);
      const normalizedData = this.normalizeMarket(validatedData);

      return normalizedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get market by ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get tags associated with a specific market.
   * 
   * This endpoint returns all tags that are associated with the specified market,
   * useful for categorization and filtering.
   * 
   * @example
   * ```typescript
   * const tags = await client.markets.getTags('123');
   * console.log('Market Tags:', tags.map(tag => tag.name));
   * 
   * // Find category-specific tags
   * const politicsTags = tags.filter(tag => tag.category === 'politics');
   * ```
   * 
   * @param marketId - Market ID
   * @returns Promise<Tag[]> - Array of tags associated with the market
   * @throws {Error} When the market is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/markets/get-market-tags-by-id} - API Documentation
   */
  async getTags(marketId: string): Promise<Tag[]> {
    try {
      const response = await this.http.get(`/markets/${marketId}/tags`);
      const validatedData = TagSchema.array().parse(response.data);

      return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tags for market ${marketId}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get a market by its human-readable slug.
   * 
   * This endpoint returns detailed information about a market using its
   * slug instead of the numeric ID. JSON-encoded arrays are automatically
   * parsed for easier consumption.
   * 
   * @example
   * ```typescript
   * const market = await client.markets.getBySlug('bitcoin-up-or-down-oct-2');
   * console.log('Market:', market.title);
   * console.log('Outcomes:', market.outcomes); // ['Yes', 'No'] - automatically parsed
   * console.log('Prices:', market.outcomePrices); // [0.6, 0.4] - automatically parsed
   * ```
   * 
   * @param slug - Market slug (human-readable identifier)
   * @returns Promise<Market> - Market data with normalized arrays
   * @throws {Error} When the market is not found or the API request fails
   * 
   * @see {@link https://docs.polymarket.com/api-reference/markets/get-market-by-slug} - API Documentation
   */
  async getBySlug(slug: string): Promise<Market> {
    try {
      const response = await this.http.get(`/markets/slug/${slug}`);
      const validatedData = MarketSchema.parse(response.data);
      const normalizedData = this.normalizeMarket(validatedData);

      return normalizedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get market by slug ${slug}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Normalize market data by parsing JSON-encoded arrays
   * @param market - Raw market data
   * @returns Market - Normalized market data
   */
  private normalizeMarket(market: Record<string, unknown>): Market {
    return {
      ...market,
      outcomes:
        parseJsonArray<string>(market.outcomes as string) ?? (market.outcomes as string | null),
      outcomePrices:
        toNumberArray(market.outcomePrices as string) ?? (market.outcomePrices as string | null),
      clobTokenIds:
        parseJsonArray<string>(market.clobTokenIds as string) ??
        (market.clobTokenIds as string | null),
    } as Market;
  }
}
